import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

import { DATE_MONTH_DAY_YEAR, RPM_PATIENT_STATUS } from '../../../constants';
import { TableRowActions } from '../../../containers/Table/Table3';
import Strings from '../../../Strings';
import { getProgramDisplayName, isAccessAlignmentPending, isAccessEligibilityPending, isAccessProgram } from '../../../utils/cmsPrograms';

const enrollmentGroups = {
  careManagement: {
    label: Strings.careManagementPrograms,
    matches: enrollment => enrollment.program && enrollment.program !== 'CTAM' && !isAccessProgram(enrollment.program),
  },
  access: {
    label: Strings.accessPrograms,
    matches: enrollment => isAccessProgram(enrollment.program),
  },
  clinicalTrials: {
    label: Strings.clinicalTrials,
    matches: enrollment => enrollment.program === 'CTAM',
  },
  enrollmentProcess: {
    label: Strings.enrollmentProcess,
    matches: enrollment => !enrollment.program,
  },
};

const statusPriority = {
  [RPM_PATIENT_STATUS.activated]: 0,
  [RPM_PATIENT_STATUS.enrolled]: 1,
  [RPM_PATIENT_STATUS.verified]: 2,
  [RPM_PATIENT_STATUS.onboarded]: 3,
  [RPM_PATIENT_STATUS.notEnrolled]: 4,
  [RPM_PATIENT_STATUS.ended]: 5,
};

const formatDate = value => value ? moment(value).format(DATE_MONTH_DAY_YEAR) : '-';

const formatActivationDates = enrollment => {
  if (!enrollment.enrollmentStart && !enrollment.enrollmentEnd) {
    return '-';
  }
  if (enrollment.enrollmentStart && enrollment.enrollmentEnd) {
    return `${formatDate(enrollment.enrollmentStart)} – ${formatDate(enrollment.enrollmentEnd)}`;
  }
  if (enrollment.enrollmentStart) {
    return `${Strings.startDate}: ${formatDate(enrollment.enrollmentStart)}`;
  }
  return `${Strings.endDate}: ${formatDate(enrollment.enrollmentEnd)}`;
};

const getReason = enrollment => {
  if (enrollment.notEnrolledReason) {
    return Strings.capPatient.notEnrolledReason[enrollment.notEnrolledReason] || enrollment.notEnrolledReason;
  }
  if (enrollment.terminatedReason) {
    return Strings.capPatient.terminatedReason?.[enrollment.terminatedReason] || enrollment.terminatedReason;
  }
  return null;
};

const getEnrollmentDetails = (enrollment, patient) => {
  const details = [];
  if (isAccessEligibilityPending(enrollment)) {
    details.push(`${Strings.eligibility}: ${Strings.accessEligibilityAwaitingConfirmation}`);
  } else if (isAccessProgram(enrollment.program) && enrollment.accessEligibility?.status === 'failed') {
    details.push(`${Strings.eligibility}: ${Strings.accessEligibilityFailed}`);
    if (enrollment.accessEligibility.errorMessage) {
      details.push(`${Strings.errorMessage}: ${enrollment.accessEligibility.errorMessage}`);
    }
  } else if (isAccessProgram(enrollment.program) && enrollment.accessEligibility?.resultCode) {
    details.push(`${Strings.eligibility}: ${enrollment.accessEligibility.resultCode}`);
  }
  if (isAccessAlignmentPending(enrollment)) {
    details.push(`${Strings.alignment}: ${Strings.accessAlignmentAwaitingConfirmation}`);
  } else if (isAccessProgram(enrollment.program) && enrollment.accessAlignment?.resultCode) {
    // Deliberately the raw CMS code: the combined status widget is a later feature.
    const resultAt = enrollment.accessAlignment.resultAt ? `, ${formatDate(enrollment.accessAlignment.resultAt)}` : '';
    details.push(`${Strings.lastAlignmentResult}: ${enrollment.accessAlignment.resultCode}${resultAt}`);
    if (enrollment.accessAlignment.status === 'failed' && enrollment.accessAlignment.errorMessage) {
      details.push(`${Strings.errorMessage}: ${enrollment.accessAlignment.errorMessage}`);
    }
  }
  if (enrollment.conditionTitle) {
    details.push(`${Strings.condition}: ${enrollment.conditionTitle}`);
  }
  if (enrollment.shipment?.status) {
    const shipmentStatus = Strings.shipping.shipmentStatuses[enrollment.shipment.status] || enrollment.shipment.status;
    details.push(`${Strings.shipping.shippingStatus}: ${shipmentStatus}`);
  }
  const reason = getReason(enrollment);
  if (reason) {
    details.push(`${Strings.reason}: ${reason}`);
  }
  if (enrollment.outreachStatus) {
    details.push(`${Strings.status}: ${Strings.extendedOutreachStatusEnum[enrollment.outreachStatus] || enrollment.outreachStatus}`);
  }
  if (enrollment.status === RPM_PATIENT_STATUS.verified) {
    const televisit = patient.enrollmentSchedules?.find(schedule => !schedule.finished);
    if (televisit) {
      details.push(`${Strings.capPatient.scheduledTelevisits}: ${formatDate(televisit.enrollAt)}`);
    }
  }
  return details.length ? details : ['-'];
};

const sortEnrollments = enrollments => [...enrollments].sort((a, b) => {
  const statusDifference = (statusPriority[a.status] ?? 99) - (statusPriority[b.status] ?? 99);
  if (statusDifference !== 0) {
    return statusDifference;
  }
  return moment(b.stateAssignedAt || 0).valueOf() - moment(a.stateAssignedAt || 0).valueOf();
});

function PatientEnrollmentsDetails({ patient, enrollmentButtons }) {
  const enrollments = Object.entries(patient.patientEnrollment?.enrollments || {}).map(([id, enrollment]) => ({
    ...enrollment,
    id,
  }));

  if (enrollments.length === 0) {
    return <div className="patient-enrollments-empty">{Strings.noDataAvailable}</div>;
  }

  return (
    <div className="patient-enrollments-details">
      <table className="table patient-enrollments-table">
        <thead>
          <tr className="table-row with-buttons">
            <th><span className='header-cell'>{Strings.program}</span></th>
            <th><span className='header-cell'>{Strings.status}</span></th>
            <th><span className='header-cell'>{Strings.stateSince}</span></th>
            <th><span className='header-cell'>{Strings.activationStartEnd}</span></th>
            <th><span className='header-cell'>{Strings.capPatient.careProvider}</span></th>
            <th><span className='header-cell'>{Strings.widgets.details}</span></th>
            <th className="patient-enrollment-actions">{Strings.actions}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(enrollmentGroups).map(([groupId, group]) => {
            const groupEnrollments = sortEnrollments(enrollments.filter(group.matches));
            if (groupEnrollments.length === 0) {
              return null;
            }
            return (
              <React.Fragment key={groupId}>
                <tr className="patient-enrollments-group-row">
                  <td colSpan={7} className="patient-enrollments-group-label">{`${group.label} (${groupEnrollments.length})`}</td>
                </tr>
                {groupEnrollments.map(enrollment => (
                  <tr key={enrollment.id} className="with-buttons patient-enrollment-row">
                    <td>{getProgramDisplayName(enrollment.program, enrollment.subprogram) || '-'}</td>
                    <td>
                      <span className={`patient-enrollment-status status-${enrollment.status || 'unknown'}`}>
                        {Strings.capPatient.patientStatus[enrollment.status] || enrollment.status || '-'}
                      </span>
                    </td>
                    <td>{formatDate(enrollment.stateAssignedAt)}</td>
                    <td>{formatActivationDates(enrollment)}</td>
                    <td>{enrollment.careProvider?.name || enrollment.careProviderOrg?.name || '-'}</td>
                    <td>
                      {getEnrollmentDetails(enrollment, patient).map(detail => <div key={detail}>{detail}</div>)}
                    </td>
                    <td className="sticky-right patient-enrollment-actions">
                      <TableRowActions
                        buttons={enrollmentButtons}
                        rowId={enrollment.id}
                        data={{ patient, enrollment, enrollmentId: enrollment.id, patientView: true }}
                      />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

PatientEnrollmentsDetails.propTypes = {
  patient: PropTypes.shape({
    enrollmentSchedules: PropTypes.array,
    patientEnrollment: PropTypes.shape({
      enrollments: PropTypes.object,
    }),
  }).isRequired,
  enrollmentButtons: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default PatientEnrollmentsDetails;
